import { useEffect, useRef, useState } from 'hono/jsx';
import { SiteShell } from '../../components/SiteShell';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { useSite } from '../../lib/site';

interface Material { name: string; size: number; role?: string; slot?: string; }

const ROLE_LABEL: Record<string, string> = { logo: 'Logo', brand_guide: 'Brand guide', image: 'Photo', copy: 'Copy' };

const IMAGE_EXTS = new Set(['jpg','jpeg','png','gif','webp','avif','svg']);

export function SiteFiles({ siteId }: { siteId: string }) {
  const { site, loading: siteLoading } = useSite(siteId);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [usageMB, setUsageMB] = useState<string>('0');
  const [uploading, setUploading] = useState<boolean>(false);
  const [pct, setPct] = useState<number>(0);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function loadMaterials() {
    try {
      const r = await api('/sites/' + siteId + '/materials');
      if (!r.ok) throw new Error('Failed to load materials');
      const data = await r.json();
      const list: Material[] = data.materials || [];
      setMaterials(list);
      const total = list.reduce((sum, m) => sum + m.size, 0);
      setUsageMB((total / 1024 / 1024).toFixed(1));
    } catch (e) { console.error('Failed to load materials:', e); }
  }

  useEffect(() => { loadMaterials(); }, [siteId]);

  async function uploadMaterials(files: FileList) {
    setUploading(true); setPct(0);
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('files', files[i]);
    try {
      const r = await api('/sites/' + siteId + '/materials/upload', { method: 'POST', body: fd });
      setPct(100);
      const result = await r.json().catch(() => ({}));
      const errs = (result as any).errors as string[] | undefined;
      const uploaded = (result as any).uploaded as string[] | undefined;
      if (errs && errs.length) showToast('warning', 'Upload Complete', errs.join(', '));
      else if (uploaded && uploaded.length) showToast('success', 'Upload Complete', `Uploaded ${uploaded.length} file(s)`);
      await loadMaterials();
    } catch (err: any) {
      showToast('error', 'Upload Failed', err?.message || 'Upload failed');
    } finally {
      setTimeout(() => setUploading(false), 1000);
    }
  }

  async function deleteMaterial(name: string) {
    if (!confirm(`Delete ${name}?`)) return;
    try {
      const r = await api(`/sites/${siteId}/materials/${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error((err as any).error || 'Failed to delete'); }
      showToast('success', 'Deleted', `${name} removed`);
      await loadMaterials();
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Delete failed');
    }
  }

  return (
    <SiteShell site={site} loading={siteLoading} active="materials">
      <div class="bg-dark-800 border border-slate-700/50 rounded-xl p-6">
        <h2 class="text-lg font-semibold text-slate-100 mb-1">Brand Materials</h2>
        <p class="text-xs text-slate-400 mb-3">Upload logos, images, and documents to use in your site builds. Max 10MB per file, 50MB total.</p>
        <div
          onClick={() => fileInput.current?.click()}
          onDragOver={(e: DragEvent) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e: DragEvent) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer && e.dataTransfer.files.length) uploadMaterials(e.dataTransfer.files); }}
          class={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-3 ${dragOver ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-600/50 hover:border-cyan-500/50'}`}
        >
          <input
            ref={fileInput} type="file" multiple accept="image/*,.pdf,.txt,.csv" class="hidden"
            onChange={(e: Event) => {
              const t = e.currentTarget as HTMLInputElement;
              if (t.files && t.files.length) uploadMaterials(t.files);
              t.value = '';
            }}
          />
          <svg class="w-8 h-8 mx-auto text-slate-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
          </svg>
          <p class="text-xs text-slate-400">Drop files here or click to upload</p>
          <p class="text-xs text-slate-500 mt-1">Images, PDFs, text files</p>
        </div>
        {uploading && (
          <div class="mb-3">
            <div class="flex items-center justify-between text-xs text-slate-400 mb-1"><span>Uploading…</span><span>{pct}%</span></div>
            <div class="h-1 bg-dark-700 rounded-full overflow-hidden"><div class="h-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }}></div></div>
          </div>
        )}
        <div class="space-y-2 max-h-60 overflow-y-auto">
          {materials.length === 0 ? (
            <div class="text-xs text-slate-500 text-center py-2">No materials uploaded yet</div>
          ) : materials.map((m) => {
            const ext = (m.name.split('.').pop() || '').toLowerCase();
            const isImage = IMAGE_EXTS.has(ext);
            return (
              <div class="flex items-center justify-between bg-dark-900 rounded p-2 group">
                <div class="flex items-center gap-2 min-w-0">
                  {isImage ? (
                    <svg class="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  ) : (
                    <svg class="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                  )}
                  <span class="text-xs text-slate-300 truncate" title={m.name}>{m.name}</span>
                  {m.role && ROLE_LABEL[m.role] && <span class="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 flex-shrink-0">{ROLE_LABEL[m.role]}{m.slot && m.slot !== 'any' ? ` · ${m.slot}` : ''}</span>}
                  <span class="text-xs text-slate-500 ml-2">{(m.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
                <button type="button" onClick={() => deleteMaterial(m.name)} class="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1" title="Delete">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                </button>
              </div>
            );
          })}
        </div>
        <div class="flex items-center justify-between text-xs bg-dark-900 rounded p-2 mt-3">
          <span class="text-slate-500">Storage used:</span>
          <span class="text-slate-300">{usageMB} / 50 MB</span>
        </div>
      </div>
    </SiteShell>
  );
}
